import api, { handleResponse, handleError } from './api';
import { refreshSession } from './sessionRefresh';
import { getStaySignedIn } from './staySignedIn';
import { inviteRefCode } from '../utils/inviteRef';
import { firstTouchPayload } from './analytics';

const authService = {
  /**
   * Register a new user
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @param {string} phoneNumber - User's phone number
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async register(email, password, phoneNumber, phoneCountry = null) {
    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        phoneNumber,
        phoneCountry,
        rememberMe: getStaySignedIn(),
        // The invite card they scanned, if any — the server records the referral once.
        ref: inviteRefCode(),
        // Where this browser first landed. Only ever fills a null on the new row.
        firstTouch: firstTouchPayload(),
      });

      // API returns { success, data: { accessToken, refreshToken, user, expiresAt } }
      const result = response.data.data || response.data;

      // Store tokens if returned
      if (result.accessToken) {
        localStorage.setItem('accessToken', result.accessToken);
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }

      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Login a user
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async login(email, password) {
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
        rememberMe: getStaySignedIn(),
      });

      // API returns { success, data: { accessToken, refreshToken, user, expiresAt } }
      const result = response.data.data || response.data;

      // Store tokens
      if (result.accessToken) {
        localStorage.setItem('accessToken', result.accessToken);
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }

      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Complete "Continue with Google" sign-in by exchanging the authorization code
   * (returned to the /auth/google/callback redirect URI) for our own tokens.
   * @param {string} code - Google authorization code from the callback query string
   * @param {string} redirectUri - The exact redirect_uri used in the auth request
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  /**
   * Sign in with Apple. The device hands us a signed identity token; the server
   * verifies it against Apple's public keys — there's no code exchange.
   *
   * `fullName` is present only on the user's FIRST authorization for this app,
   * ever, so it's forwarded as-is and the server keeps it only when creating an
   * account.
   */
  async appleLogin(identityToken, fullName = null, platform = 'native') {
    try {
      const response = await api.post('/auth/apple', { identityToken, fullName, platform, rememberMe: getStaySignedIn(), ref: inviteRefCode(), firstTouch: firstTouchPayload() });
      const result = response.data.data || response.data;

      if (result.accessToken) localStorage.setItem('accessToken', result.accessToken);
      if (result.refreshToken) localStorage.setItem('refreshToken', result.refreshToken);

      return { success: true, data: result };
    } catch (error) {
      return handleError(error);
    }
  },

  async googleLogin(code, redirectUri, codeVerifier = null, platform = null) {
    try {
      // codeVerifier is sent only by the native app: its iOS OAuth client is a
      // public client with no secret, so PKCE is what authenticates the exchange.
      const response = await api.post('/auth/google', { code, redirectUri, codeVerifier, platform, rememberMe: getStaySignedIn(), ref: inviteRefCode(), firstTouch: firstTouchPayload() });

      // API returns { success, data: { accessToken, refreshToken, user, expiresAt } }
      const result = response.data.data || response.data;

      if (result.accessToken) {
        localStorage.setItem('accessToken', result.accessToken);
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }

      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Sign in from an ID token the device already holds — Android's system account
   * picker. There is no code to exchange and no redirect to come back from: Google
   * disabled custom URI schemes for Android OAuth clients, so the browser flow above
   * is refused before consent. The server verifies the JWT against Google's keys.
   *
   * @param {string} idToken
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async googleIdTokenLogin(idToken) {
    try {
      const response = await api.post('/auth/google', { idToken, rememberMe: getStaySignedIn(), ref: inviteRefCode(), firstTouch: firstTouchPayload() });
      const result = response.data.data || response.data;

      if (result.accessToken) {
        localStorage.setItem('accessToken', result.accessToken);
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }

      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Logout the current user
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async logout() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');

      // Call logout endpoint if we have a refresh token
      if (refreshToken) {
        await api.post('/auth/logout', { refreshToken });
      }

      // Clear local storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

      return {
        success: true,
        data: { message: 'Logged out successfully' },
        error: null,
      };
    } catch (error) {
      // Still clear local storage even if API call fails
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

      return handleError(error);
    }
  },

  /**
   * Refresh the active session — the one shared refresh (`services/sessionRefresh`),
   * never a second POST of the single-use token.
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async refreshToken() {
    const result = await refreshSession();
    if (result.accessToken) return { success: true, data: result.data || { accessToken: result.accessToken }, error: null };
    return { success: false, data: null, error: result.rejected ? 'Session expired' : 'Could not reach the server' };
  },

  /**
   * Request a password reset email
   * @param {string} email - User's email
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async forgotPassword(email) {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Reset password with token
   * @param {string} token - Password reset token
   * @param {string} newPassword - New password
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword,
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // ── Email change (dual-authorization OTP) ────────────────────────────────
  /** Start an email change — issues OTPs to the new + current addresses. */
  async startEmailChange(newEmail) {
    try {
      return handleResponse(await api.post('/auth/email/change/start', { newEmail }));
    } catch (error) {
      return handleError(error);
    }
  },

  /** Verify the OTP sent to the NEW address. */
  async verifyNewEmailOtp(otp) {
    try {
      return handleResponse(await api.post('/auth/email/change/verify-new', { otp }));
    } catch (error) {
      return handleError(error);
    }
  },

  /** Confirm the OTP sent to the CURRENT address. Commits when both are done. */
  async confirmCurrentEmailOtp(otp) {
    try {
      return handleResponse(await api.post('/auth/email/change/confirm-current', { otp }));
    } catch (error) {
      return handleError(error);
    }
  },

  /** Resumable status of a pending email change (or null). */
  async getEmailChangeStatus() {
    try {
      return handleResponse(await api.get('/auth/email/change/status'));
    } catch (error) {
      return handleError(error);
    }
  },

  /** Abandon a pending email change. */
  async cancelEmailChange() {
    try {
      return handleResponse(await api.post('/auth/email/change/cancel'));
    } catch (error) {
      return handleError(error);
    }
  },

  // ── Phone verification + reset-by-phone ──────────────────────────────────
  // Number and country always travel separately: the server resolves the dial
  // code from the ISO country, so a request can't smuggle its own prefix.

  /** Claim a number and have a 6-digit code texted to it. */
  async startPhoneVerification(phoneNumber, countryIso2) {
    try {
      return handleResponse(await api.post('/auth/phone/start', { phoneNumber, countryIso2 }));
    } catch (error) {
      return handleError(error);
    }
  },

  /** Prove the code — this is what marks the number verified. */
  async verifyPhoneOtp(otp) {
    try {
      return handleResponse(await api.post('/auth/phone/verify', { otp }));
    } catch (error) {
      return handleError(error);
    }
  },

  /** Drop the number from the account (and void any code in flight). */
  async removePhone() {
    try {
      return handleResponse(await api.delete('/auth/phone'));
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Ask for a reset code by phone. Always succeeds, whether or not the number is
   * on an account — the UI must not report otherwise, or it becomes a way to test
   * which numbers are registered.
   */
  async forgotPasswordByPhone(phoneNumber, countryIso2) {
    try {
      return handleResponse(await api.post('/auth/forgot-password/phone', { phoneNumber, countryIso2 }));
    } catch (error) {
      return handleError(error);
    }
  },

  /** Redeem the reset code and set a new password. Revokes every session. */
  async resetPasswordByPhone(phoneNumber, countryIso2, otp, newPassword) {
    try {
      return handleResponse(await api.post('/auth/reset-password/phone', {
        phoneNumber, countryIso2, otp, newPassword,
      }));
    } catch (error) {
      return handleError(error);
    }
  },
};

export default authService;
